/**
 * Configuration Resolver for Client Web App
 * Handles server-rendered injection (id="vs-config"), local storage cache, and defaults.
 */
export function getAppConfig() {
  const embedded = document.getElementById('vs-config');
  if (embedded && embedded.textContent) {
    try {
      const parsed = JSON.parse(embedded.textContent);
      if (parsed && typeof parsed === 'object') {
        try {
          localStorage.setItem('vs:site_config', JSON.stringify({ name: parsed.site?.name, design: parsed.design }));
        } catch {}
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to parse embedded vs-config:', e);
    }
  }

  // Standalone mode default settings
  let siteName = 'ZXNITE';
  const siteDomain = import.meta.env?.VITE_SITE_DOMAIN || window.location.hostname || 'zxnite.com';

  const defaultDesign = {
    'seo.ga4': import.meta.env?.VITE_GA4 || '',
    'seo.gtm': import.meta.env?.VITE_GTM || '',
    'brand.name': siteName,
    'brand.tagline': 'xnite',
    'brand.short_name': 'xnite',
    'look.mode': 'dark',
    'look.accent': '#34c46b',
    'look.accent_ink': '#04180c',
    'look.dark_bg': '#0b140f',
    'look.dark_surface': '#132119',
    'look.dark_text': '#e8f3ec',
    'look.radius': 'soft',
    'look.width': '1280',
    'cinema.hover': 'lift',
    'cinema.hero_dim': 'medium',
    'cinema.sidebar': 'slide',
    'seo.title': '{page} | {name}',
    'seo.description': 'Watch trending vertical short dramas, mini series, and web episodes in full HD.',
    'text.home': 'Home',
    'text.browse': 'Browse',
    'text.menu': 'Menu',
    'text.skip': 'Skip to content',
    'text.search_placeholder': 'Search dramas...',
    'text.search_button': 'Search',
    'text.watch_now': 'Watch now',
    'text.continue': 'Continue',
    'text.continue_watching': 'Continue watching',
    'text.see_all': 'See all',
    'text.episodes': 'Episodes',
    'text.episode': 'Episode',
    'text.next_episode': 'Next episode',
    'text.previous_episode': 'Previous',
    'text.up_next': 'Next episode starts in',
    'text.all_genres': 'All genres',
    'text.all_tags': 'All tags',
    'text.all_platforms': 'All platforms',
    'text.sort_by': 'Sort by',
    'text.sort_rank': 'Popular',
    'text.sort_rating': 'Top rated',
    'text.sort_episodes': 'Most episodes',
    'text.load_more': 'Show more',
    'text.no_results': 'Nothing matches that. Try another word or filter.',
    'text.loading': 'Loading dramas...',
    'text.error': 'Something went wrong. Please try again.',
    'text.retry': 'Try again',
    'text.not_found_title': 'Page not found',
    'text.not_found_text': 'That page does not exist, or has been taken down.',
    'text.unavailable': 'This episode is not available right now.',
    'text.h265_notice': 'This video needs a browser that can play H.265, such as Safari.',
    'text.h265_badge': 'H.265',
    'text.ad_label': 'Advertisement',
    'text.sponsored': 'Sponsored',
    'text.partner_button': 'Learn more',
    'notice.enabled': true,
    'notice.mode': 'consent',
    'notice.title': 'We use cookies',
    'notice.text': 'Essential cookies remember your settings and playback progress. With your OK we also use statistics and advertising cookies to improve your experience and show ads.',
    'notice.button': 'Accept all',
    'notice.decline': 'Essential only',
    'notice.link_label': 'Cookie policy',
    'notice.link': '/page/privacy'
  };

  // Restore client cached settings from previous session to avoid any flash
  try {
    const cached = localStorage.getItem('vs:site_config');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        if (parsed.name && !parsed.name.toLowerCase().includes('mshorts')) {
          siteName = parsed.name;
        } else {
          localStorage.removeItem('vs:site_config');
        }
        if (parsed.design) {
          if (parsed.design['brand.name'] && parsed.design['brand.name'].toLowerCase().includes('mshorts')) {
            delete parsed.design['brand.name'];
          }
          Object.assign(defaultDesign, parsed.design);
        }
      }
    }
  } catch {}

  return {
    template: 'cinema',
    mount: '/',
    root: '/',
    lang: 'en',
    languages: [],
    site: {
      name: siteName,
      domain: siteDomain
    },
    design: defaultDesign,
    pages: [
      { slug: 'about', title: 'About Us' },
      { slug: 'terms', title: 'Terms of Service' },
      { slug: 'privacy', title: 'Privacy Policy' },
      { slug: 'dmca', title: 'DMCA' },
      { slug: 'contact', title: 'Contact' }
    ]
  };
}
